import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { MdMoreVert, MdDeleteForever, MdEdit, MdAdd, MdCheck } from "react-icons/md";
import { Menu } from "@headlessui/react";

import api from "@/services/api";

export default ({ answer, setAnswer }) => {
  const [editNote, setEditNote] = useState(null);

  async function addNote() {
    const notes = [...(answer.notes || [])];
    notes.push({ critere: "", pourcentage: null, note_estimee: null, note_reelle: null });
    const { ok, data } = await api.put(`/mission_ao_answer/${answer._id}`, { notes });
    if (!ok) throw new Error("Failed to add note");
    setAnswer(data);
    setEditNote(notes[notes.length - 1]);
  }

  async function removeNote(index) {
    try {
      const notes = [...answer.notes];
      notes.splice(index, 1);
      const { ok, data } = await api.put(`/mission_ao_answer/${answer._id}`, { notes });
      if (!ok) throw new Error("Failed to remove note");
      setAnswer(data);
      toast.success("Note removed successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error removing note");
    }
  }

  function calculateTotals() {
    if (!answer.notes || answer.notes.length === 0) {
      return { totalEstimee: 0, totalReelle: 0, totalPourcentage: 0 };
    }

    return answer.notes.reduce(
      (acc, note) => {
        const pourcentage = note.pourcentage || 0;
        const noteEstimee = note.note_estimee || 0;
        const noteReelle = note.note_reelle || 0;

        return {
          totalEstimee: acc.totalEstimee + noteEstimee,
          totalReelle: acc.totalReelle + noteReelle,
          totalPourcentage: acc.totalPourcentage + pourcentage,
        };
      },
      { totalEstimee: 0, totalReelle: 0, totalPourcentage: 0 },
    );
  }

  const { totalEstimee, totalReelle, totalPourcentage } = calculateTotals();
  const scorePercentage = totalReelle > 0 ? (totalReelle / 100) * 100 : 0;
  const scoreDifference = totalReelle - totalEstimee;

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm">
      <EditNoteModal
        note={editNote}
        setNote={async (e) => {
          if (!e) return setEditNote(null);
          const notes = [...answer.notes];
          notes[notes.findIndex((n) => n._id === e._id)] = e;
          const { ok, data } = await api.put(`/mission_ao_answer/${answer._id}`, { notes });
          if (!ok) throw new Error("Failed to update answer");
          toast.success("Note updated successfully!");
          setAnswer(data);
          setEditNote(null);
        }}
      />

      <div className="mb-8 flex flex-wrap items-center justify-between border-b border-gray-100 pb-5">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-1">Grille d'évaluation</h2>
          <p className="text-gray-500">Optimisez vos chances de réussite en analysant votre performance</p>
        </div>
      </div>

      {totalReelle > 0 && (
        <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-600 uppercase">Note globale</h3>
            </div>
            <div className="p-5">
              <div className="flex items-center">
                <div className="text-3xl font-bold text-indigo-600">{totalReelle.toFixed(1)}</div>
                <div className="text-xl text-gray-400 font-medium ml-1">/100</div>
              </div>
              <div className={`text-sm mt-2 flex items-center ${scoreDifference >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                <span
                  className="inline-block h-4 w-4 rounded-full mr-1.5 bg-opacity-20 flex items-center justify-center"
                  style={{ backgroundColor: scoreDifference >= 0 ? "rgba(16, 185, 129, 0.2)" : "rgba(225, 29, 72, 0.2)" }}>
                  {scoreDifference >= 0 ? "+" : "-"}
                </span>
                {Math.abs(scoreDifference).toFixed(1)} points vs estimation
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-600 uppercase">Taux de réussite</h3>
            </div>
            <div className="p-5">
              <div className="flex items-center">
                <div className="text-3xl font-bold text-indigo-600">{scorePercentage.toFixed(0)}%</div>
              </div>
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${scorePercentage >= 70 ? "bg-emerald-500" : scorePercentage >= 50 ? "bg-amber-500" : "bg-rose-500"}`}
                    style={{ width: `${scorePercentage}%` }}></div>
                </div>
                <div className="text-sm text-gray-500 mt-2 flex items-center">
                  <span
                    className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
                      scorePercentage >= 70 ? "bg-emerald-500" : scorePercentage >= 50 ? "bg-amber-500" : "bg-rose-500"
                    }`}></span>
                  {scorePercentage >= 70 ? "Excellente position" : scorePercentage >= 50 ? "Position compétitive" : "Nécessite amélioration"}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-600 uppercase">Couverture des critères</h3>
            </div>
            <div className="p-5">
              <div className="flex items-center">
                <div className={`text-3xl font-bold ${totalPourcentage === 100 ? "text-indigo-600" : "text-amber-500"}`}>{totalPourcentage}%</div>
              </div>
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${totalPourcentage === 100 ? "bg-emerald-500" : "bg-amber-500"}`}
                    style={{ width: `${totalPourcentage}%`, maxWidth: "100%" }}></div>
                </div>
                <div className="text-sm text-gray-500 mt-2 flex items-center">
                  {totalPourcentage === 100 ? (
                    <div className="flex items-center text-emerald-600">
                      <MdCheck className="w-4 h-4 mr-1" /> Couverture complète
                    </div>
                  ) : (
                    <div className="text-amber-600">Objectif: 100% ({100 - totalPourcentage}% restant)</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200 flex justify-between items-center">
          <h3 className="font-semibold text-gray-700">Détail des critères d'évaluation</h3>
          <button
            onClick={addNote}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-md px-4 py-2 flex items-center justify-center text-sm font-medium shadow-sm transition-all hover:shadow-md">
            <MdAdd className="w-4 h-4 mr-1.5" /> Ajouter un critère
          </button>
        </div>

        {(answer.notes || []).length === 0 ? (
          <div className="text-center p-10 border-b border-gray-200 bg-white">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-50 text-indigo-500 mb-4">
              <MdAdd className="w-8 h-8" />
            </div>
            <p className="text-gray-600 font-medium mb-2">Aucun critère d'évaluation défini</p>
            <p className="text-gray-500 text-sm max-w-md mx-auto">Ajoutez des critères pour suivre votre performance et augmenter vos chances de succès dans l'appel d'offre.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 bg-gray-50">Critère</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 bg-gray-50">Importance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 bg-gray-50">Note Estimée</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 bg-gray-50">Note Réelle</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 bg-gray-50 w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(answer.notes || []).map((note, index) => {
                  const difference = (note.note_reelle || 0) - (note.note_estimee || 0);
                  return (
                    <tr key={index} onClick={(e) => e.stopPropagation()} className="hover:bg-gray-50 border-b border-gray-200 last:border-b-0">
                      <td className="px-6 py-4 bg-white">
                        <span className="font-medium text-gray-800">{note.critere || "—"}</span>
                      </td>
                      <td className="px-6 py-4 bg-white">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">{note.pourcentage || 0}%</span>
                      </td>
                      <td className="px-6 py-4 bg-white">
                        <span className="text-gray-600">{note.note_estimee || "—"}</span>
                      </td>
                      <td className="px-6 py-4 bg-white">
                        <div className="flex items-center">
                          <span className="font-medium">{note.note_reelle || "—"}</span>
                          {note.note_reelle && note.note_estimee && (
                            <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${difference >= 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                              {difference >= 0 ? "+" : ""}
                              {difference.toFixed(1)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center bg-white">
                        <Menu as="div" className="relative inline-block text-left">
                          <Menu.Button className="p-1.5 hover:bg-gray-100 rounded-full">
                            <MdMoreVert className="text-gray-500 w-5 h-5" />
                          </Menu.Button>
                          <Menu.Items className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 ring-1 ring-black ring-opacity-5 py-1 focus:outline-none">
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  className={`${active ? "bg-indigo-50 text-indigo-700" : "text-indigo-600"} flex w-full items-center px-4 py-2 text-sm`}
                                  onClick={() => setEditNote(note)}>
                                  <MdEdit className="mr-2 w-5 h-5" /> Modifier
                                </button>
                              )}
                            </Menu.Item>
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  className={`${active ? "bg-red-50 text-red-700" : "text-red-600"} flex w-full items-center px-4 py-2 text-sm`}
                                  onClick={() => removeNote(index)}>
                                  <MdDeleteForever className="mr-2 w-5 h-5" /> Supprimer
                                </button>
                              )}
                            </Menu.Item>
                          </Menu.Items>
                        </Menu>
                      </td>
                    </tr>
                  );
                })}

                <tr className={`font-semibold ${totalPourcentage !== 100 ? "bg-amber-50" : "bg-indigo-50"}`} onClick={(e) => e.stopPropagation()}>
                  <td className="px-6 py-4 border-t border-gray-300">
                    <span className="text-gray-800">Total</span>
                  </td>
                  <td className="px-6 py-4 border-t border-gray-300">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        totalPourcentage === 100 ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
                      }`}>
                      {totalPourcentage}%
                    </span>
                  </td>
                  <td className="px-6 py-4 border-t border-gray-300">{totalEstimee.toFixed(2)}</td>
                  <td className="px-6 py-4 border-t border-gray-300">{totalReelle.toFixed(2)}</td>
                  <td className="px-6 py-4 border-t border-gray-300">
                    {totalPourcentage !== 100 && <span className="text-xs text-amber-600 font-normal">Le total doit être 100%</span>}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const EditNoteModal = ({ note, setNote }) => {
  const [editedNote, setEditedNote] = useState(null);

  useEffect(() => {
    setEditedNote({ ...note });
  }, [note]);

  if (!note) return null;
  if (!editedNote) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Modifier le critère</h2>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setNote(editedNote);
          }}>
          <div className="p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Critère d'évaluation</label>
              <textarea
                type="text"
                rows={3}
                value={editedNote.critere}
                onChange={(e) => setEditedNote({ ...editedNote, critere: e.target.value })}
                placeholder="Décrivez le critère d'évaluation..."
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Importance (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={editedNote.pourcentage || ""}
                onChange={(e) => setEditedNote({ ...editedNote, pourcentage: parseFloat(e.target.value) })}
                placeholder="Ex: 25"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">Pourcentage d'importance du critère (total doit être 100%)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Note Estimée</label>
              <input
                type="number"
                min="0"
                max="100"
                value={editedNote.note_estimee || ""}
                onChange={(e) => setEditedNote({ ...editedNote, note_estimee: parseFloat(e.target.value) })}
                placeholder="Ex: 80"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">Votre estimation de votre performance sur ce critère</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Note Réelle</label>
              <input
                type="number"
                min="0"
                max="100"
                value={editedNote.note_reelle || ""}
                onChange={(e) => setEditedNote({ ...editedNote, note_reelle: parseFloat(e.target.value) })}
                placeholder="Ex: 85"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">La note effectivement obtenue sur ce critère</p>
            </div>
          </div>
          <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setNote(null)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
